/**
 * Sample data for `VITE_INTERNAL_FIXTURES=1`.
 *
 * These are NOT invented pleasant numbers. They are shaped from what the product
 * repo documents about the real platform as of 2026-08-28, because a fixture
 * that shows a healthy business would train everyone to read the wrong screen:
 *
 *   · 184,475 tokens charged for optimization; 97,674 refunded — 52.9%.
 *   · 400,000 tokens credited as `purchase`, across FOUR rows, ALL of them
 *     100%-off partner coupons. External cash collected is $0.
 *   · token_ledger.usd_amount was added on 2026-08-28 with no backfill, so
 *     every historic purchase has unknown cash and the figure is a FLOOR.
 *   · developers.infra_cost_monthly is EMPTY, so margin is undefined — not zero.
 *   · 99.7% of storage belongs to two internal organizations.
 *
 * If a panel looks wrong against this data, the panel is probably right.
 */
import type { MoneyLadder } from '@/domains/money/types'
import type { TokenFlows } from '@/domains/tokens/types'
import type { OrgRoster } from '@/domains/orgs/types'
import type { DailyPoint, OrgMetrics, OrgMoney } from '@/domains/metrics/types'
import type { Freshness, PlatformSummary } from '@/domains/platform/types'
import type { ScenarioBaseline } from '@/domains/scenarios/types'
import type { SupportQueue, SupportThread, SupportTicketDetail } from '@/domains/support/types'
import type { AccessAction, AccessHistory, AccessQueue } from '@/domains/access/types'
import type { ContactAction, ContactCounts, ContactQueue } from '@/domains/contacts/types'

const meta = (sources: PlatformSummary['meta']['sources'], ms = 41) => ({
  generated_at: '2026-09-01T18:00:00Z',
  query_ms: ms,
  sources,
  tz: 'UTC',
})

export const moneyLadder: MoneyLadder = {
  // La moneda que el servidor siempre mandó y el tipo nunca declaró.
  currency: 'compute',
  meta: meta(['LEDGER']),
  month: '2026-08-01',
  // 184,475 charged - 97,674 refunded = 86,801 net tokens x $0.05 list.
  list_value_usd: 4340.05,
  // All of it. Every token that moved was courtesy.
  discounts_usd: 4340.05,
  cash_collected_usd: 0,
  cash_is_floor: true,
  purchases_with_unknown_cash: 4,
  direct_cost_usd: 781.2,
  contribution_usd: -781.2,
  // infra_cost_monthly is empty. NULL, never 0.
  margin_usd: null,
  infra_cost_loaded: false,
  infra_cost_usd: 2139.81,
  margin_ex_tax_usd: null,
  infra_cost_ex_tax_usd: 1844.7,
  infra_tax_usd: 295.11,
  infra_days_covered: 13,
  infra_days_in_month: 31,
}


export const tokenFlows: TokenFlows = {
  meta: meta(['LEDGER'], 63),
  granularity: 'day',
  buckets: Array.from({ length: 30 }).flatMap((_, i) => {
    const d = new Date(Date.UTC(2026, 7, 2 + i)).toISOString().slice(0, 10)
    const wave = Math.sin(i / 4) * 0.4 + 1
    return [
      { bucket: d, reason: 'optimization' as const, tokens: -Math.round(5800 * wave) },
      { bucket: d, reason: 'sandbox_run' as const, tokens: -Math.round(320 * wave) },
      { bucket: d, reason: 'basket_simulate' as const, tokens: -Math.round(190 * wave) },
      { bucket: d, reason: 'agent_chat' as const, tokens: -Math.round(45 * wave) },
      { bucket: d, reason: 'refund' as const, tokens: Math.round(3100 * wave) },
    ]
  }),
}


/**
 * Eight more customer organizations, generated deterministically.
 *
 * ── WHY THE FIXTURES GREW ───────────────────────────────────────────────────
 * The dashboard draws distributions, and six rows cannot show a distribution:
 * a histogram of six values is a bar chart with gaps and a scatter of six points
 * has no shape to read. Production has eleven external organizations, so the
 * fixtures now carry eleven — enough to review the panels honestly at the size
 * they will actually run at.
 *
 * Deterministic, never random: a fixture that changes between runs cannot be
 * compared against a screenshot taken yesterday. And the SHAPE is the real one
 * — a long tail, one dominant customer, several that barely moved — because a
 * uniform distribution would make every panel look like it works.
 */
const EXTRA_ORGS = [
  { id: '7', name: 'Aurelia Partners', slug: 'aurelia', members: 5, tokens: 28_400, ai: 2_100, bytes: 412_000_000 },
  { id: '8', name: 'Brightwater Fund', slug: 'brightwater', members: 2, tokens: 9_800, ai: 0, bytes: 96_000_000 },
  { id: '9', name: 'Cendra Asset Mgmt', slug: 'cendra', members: 8, tokens: 61_200, ai: 5_400, bytes: 1_240_000_000 },
  { id: '10', name: 'Delta Rho Capital', slug: 'delta-rho', members: 1, tokens: 640, ai: 0, bytes: 12_000_000 },
  { id: '11', name: 'Éforo Inversiones', slug: 'eforo', members: 3, tokens: 15_300, ai: 880, bytes: 205_000_000 },
  { id: '12', name: 'Ferrán Quant', slug: 'ferran', members: 4, tokens: 33_900, ai: 1_450, bytes: 388_000_000 },
  { id: '13', name: 'Grisalva Research', slug: 'grisalva', members: 2, tokens: 4_100, ai: 0, bytes: 44_000_000 },
  { id: '14', name: 'Halcón Sistemas', slug: 'halcon', members: 6, tokens: 0, ai: 0, bytes: 8_000_000 },
] as const

export const orgRoster: OrgRoster = {
  meta: meta(['LEDGER', 'EXTERNAL'], 88),
  rows: [
    { id: '1', name: 'Momento Capital', slug: 'momento-capital', org_class: 'external', created_at: '2026-04-11T00:00:00Z', members: 6, token_balance: 182_400, ai_token_balance: 12_800, last_activity_at: '2026-09-01T16:20:00Z', bytes_est: 232_000_000_000 },
    { id: '2', name: 'Fintela', slug: 'fintela', org_class: 'internal', created_at: '2026-04-11T00:00:00Z', members: 4, token_balance: 96_100, ai_token_balance: 8_400, last_activity_at: '2026-09-01T17:55:00Z', bytes_est: 151_000_000_000 },
    { id: '3', name: 'EdgeBridge Capital', slug: 'edgebridge', org_class: 'external', created_at: '2026-06-02T00:00:00Z', members: 3, token_balance: 41_250, ai_token_balance: 1_900, last_activity_at: '2026-08-27T11:05:00Z', bytes_est: 640_000_000 },
    { id: '4', name: 'Northgate Research', slug: 'northgate', org_class: 'external', created_at: '2026-07-18T00:00:00Z', members: 2, token_balance: 380, ai_token_balance: 0, last_activity_at: '2026-08-04T09:12:00Z', bytes_est: 88_000_000 },
    { id: '5', name: 'test_qa_sandbox', slug: 'test-qa', org_class: 'test', created_at: '2026-05-20T00:00:00Z', members: 1, token_balance: 400, ai_token_balance: 200, last_activity_at: null, bytes_est: 2_000_000 },
    { id: '6', name: 'Calder & Voss', slug: 'calder-voss', org_class: 'unknown', created_at: '2026-08-29T00:00:00Z', members: 1, token_balance: 400, ai_token_balance: 200, last_activity_at: '2026-08-30T14:00:00Z', bytes_est: 1_100_000 },
    ...EXTRA_ORGS.map((o) => ({
      id: o.id, name: o.name, slug: o.slug, org_class: 'external' as const,
      created_at: '2026-06-15T00:00:00Z', members: o.members,
      token_balance: o.tokens, ai_token_balance: o.ai,
      last_activity_at: '2026-08-28T10:00:00Z', bytes_est: o.bytes,
    })),
  ],
  // Parcial, como lo manda el servidor: solo aparecen las clases que el
  // filtro realmente ocultó. `external: 0` era una clave que nunca existe.
  excluded: { internal: 2, test: 1, unknown: 1 },
}


export const freshness: Freshness = {
  meta: meta(['LEDGER']),
  // The six jobs the SERVER actually reports — the five in
  // infra/terraform/live/internal/schedules.tf plus `watch-rules`, which has no
  // EventBridge rule and only runs by hand.
  //
  // The old fixture had an `aurora` row with `expected_every_hours: 0` rendered
  // as "en vivo". It is gone: Aurora is read directly on every request and was
  // never a collector, so a row for it in a COLLECTOR freshness table invented a
  // source. That fixture also used the CLIENT's field names against a server
  // that sent different ones, which is precisely why the shape drift survived —
  // fixture mode is where anyone would have seen it.
  rows: [
    { job: 'rollup-nightly', label: 'Rollup nocturno', as_of: '2026-09-01T07:00:00Z', status: 'ok',
      rows_written: 24, external_requests: 0, age_hours: 11, expected_every_hours: 24, freshness: 'fresh' },
    { job: 'stripe-sync', label: 'Stripe', as_of: '2026-09-01T06:00:00Z', status: 'ok',
      rows_written: 2, external_requests: 6, age_hours: 12, expected_every_hours: 24, freshness: 'fresh' },
    { job: 'cost-daily', label: 'AWS Cost Explorer', as_of: null, status: null,
      rows_written: null, external_requests: null, age_hours: null, expected_every_hours: 24, freshness: 'never' },
    // Cadence 744 h but late only past 768: the fourth field of JOBS. At 750 h
    // this row is still «al día», which is the case worth being able to see.
    { job: 'cost-monthly', label: 'AWS meses cerrados', as_of: '2026-08-08T10:00:00Z', status: 'ok',
      rows_written: 96, external_requests: 3, age_hours: 750, expected_every_hours: 744, freshness: 'fresh' },
    { job: 'keycloak-users', label: 'Keycloak Admin', as_of: '2026-08-31T12:00:00Z', status: 'ok',
      rows_written: 17, external_requests: 1, age_hours: 30, expected_every_hours: 6, freshness: 'late' },
    { job: 'watch-rules', label: 'Reglas de vigilancia', as_of: '2026-09-01T07:05:00Z', status: 'ok',
      rows_written: 3, external_requests: 0, age_hours: 11, expected_every_hours: null, freshness: 'unscheduled' },
  ],
}

export const scenarioBaseline: ScenarioBaseline = {
  meta: meta(['LEDGER']),
  fingerprint: 'cal:9f2a41',
  costs: [
    { operation: 'optimization', base_cost: 1, multipliers: { cpu: 0.3, mem: 0.3 } },
    { operation: 'backtest', base_cost: 1, multipliers: {} },
    { operation: 'sandbox_run', base_cost: 1, multipliers: {} },
    { operation: 'basket_simulate', base_cost: 2, multipliers: {} },
    { operation: 'agent_chat', base_cost: 0, multipliers: {} },
  ],
  token_usd_compute: 0.05,
  token_usd_ai: 0.01,
  capacity_profile: 'seed',
  infra_floor_usd_month: 1637,
  measured: { orgs_counted: 2, users_per_org: 2.5, studies_per_org_month: 6, trials_per_study: 1000 },
}

/**
 * The funnel, with the shapes that matter rather than a tidy cascade: one
 * customer that went all the way, one that stalled right after signing up, one
 * that created a study and never ran it, and one that reached `completed` with
 * NO ledger movement at all — the rung-skipper the real data contains.
 */
export const funnel = {
  rows: [
    { id: 'f-1', name: 'Momento Capital', org_class: 'external', registered_at: '2026-04-13T16:33:28Z',
      members: 6, studies: 67, studies_launched: 67, studies_completed: 62, token_balance: 13096,
      last_ledger_at: '2026-09-02T05:04:25Z' },
    { id: 'f-2', name: 'ingjars', org_class: 'external', registered_at: '2026-05-20T19:57:14Z',
      members: 1, studies: 3, studies_launched: 3, studies_completed: 1, token_balance: 0,
      last_ledger_at: null },
    { id: 'f-3', name: 'kjgkjg', org_class: 'external', registered_at: '2026-08-04T18:42:38Z',
      members: 1, studies: 1, studies_launched: 0, studies_completed: 0, token_balance: 200,
      last_ledger_at: '2026-08-04T18:42:38Z' },
    { id: 'f-4', name: 'Diego Crprt', org_class: 'external', registered_at: '2026-08-26T04:13:13Z',
      members: 1, studies: 0, studies_launched: 0, studies_completed: 0, token_balance: 100,
      last_ledger_at: '2026-08-26T04:13:13Z' },
    { id: 'f-5', name: 'Manuel Personal', org_class: 'external', registered_at: '2026-05-15T22:52:54Z',
      members: 1, studies: 0, studies_launched: 0, studies_completed: 0, token_balance: 0,
      last_ledger_at: null },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 4, sources: ['LEDGER'], tz: 'UTC' },
}

/**
 * Costos por mes, para poder ver los TRES estados de la reformulación.
 *
 * Sin esto la pantalla contesta el mismo mes elijas el que elijas, y dos de sus
 * tres estados son inalcanzables — incluido el que un defecto real escondía:
 * un mes SIN foto diaria cuya re-pregunta mensual es la única medición que
 * existe. En producción junio y julio fueron exactamente eso hasta que se
 * rellenó la foto, así que hoy no queda ningún mes así y fixtures es el único
 * lugar donde se puede revisar.
 */
export function costOverviewFor(params: Record<string, unknown>) {
  const month = typeof params.month === 'string' ? params.month : null
  if (month === '2026-06-01') return costOverviewRestatedOnly
  if (month === '2026-05-01') return costOverviewNeverAsked
  return costOverview
}

/** Medido y pagado, pero sin foto diaria: la re-pregunta es todo lo que hay. */
export const costOverviewRestatedOnly = {
  meta: meta(['MEASURED'], 18),
  month: '2026-06-01',
  usd: 0, usd_ex_tax: 0, tax_usd: 0,
  days_covered: 0, days_in_month: 30,
  untagged_usd: 0,
  by_service: [], by_component: [], by_tier: [], daily: [],
  restated_usd: 2710.37,
  restated_at: '2026-09-04T17:37:42Z',
}

/** Ni foto diaria ni re-pregunta. «No hemos mirado» no es «no se gastó». */
export const costOverviewNeverAsked = {
  meta: meta(['MEASURED'], 14),
  month: '2026-05-01',
  usd: 0, usd_ex_tax: 0, tax_usd: 0,
  days_covered: 0, days_in_month: 31,
  untagged_usd: 0,
  by_service: [], by_component: [], by_tier: [], daily: [],
  restated_usd: null,
  restated_at: null,
}

/** Costos: un mes COMPLETO, con la porción sin atribuir bien visible. */
export const costOverview = {
  month: '2026-08-01',
  usd: 3117.6, usd_ex_tax: 2687.59, tax_usd: 430.01,
  days_covered: 31, days_in_month: 31,
  untagged_usd: 681.07,
  // Un mes cerrado que YA se volvió a preguntar, y bajó: es el caso que el
  // panel existe para enseñar. Un crédito de $412.60 aterrizó tres semanas
  // después de que la factura se cerrara, y la cifra publicada arriba no se
  // movió — la resta es el dato.
  restated_usd: 2_705.00,
  restated_at: '2026-09-08T10:00:00Z',
  by_service: [
    { key: 'Amazon Relational Database Service', usd: 1698.38 },
    { key: 'Amazon Elastic Container Service', usd: 734.73 },
    { key: 'Tax', usd: 430.01 },
    { key: 'AmazonCloudWatch', usd: 111.84 },
    { key: 'EC2 - Other', usd: 41.96 },
    { key: 'otros', usd: 100.68 },
  ],
  by_component: [
    { key: 'db', usd: 1662.84 },
    { key: '(untagged)', usd: 681.07 },
    { key: 'strategy-sandbox', usd: 162.5 },
    { key: 'backend', usd: 151.78 },
    { key: 'otros', usd: 459.41 },
  ],
  by_tier: [
    { key: 'data', usd: 1667.42 },
    { key: 'compute', usd: 499.13 },
    { key: '(untagged)', usd: 690.94 },
    { key: 'app', usd: 182.49 },
    { key: 'platform', usd: 68.5 },
    { key: 'billing', usd: 9.18 },
  ],
  daily: Array.from({ length: 31 }, (_, i) => ({
    day: `2026-08-${String(i + 1).padStart(2, '0')}`,
    usd: 95 + (i % 7) * 6,
  })),
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 12, sources: ['EXTERNAL'], tz: 'UTC' },
}

/** Personas: alguien activo, alguien callado, alguien sin rastro, y un
 *  desacuerdo entre la app y Keycloak. */
export const people = {
  login_history_available: false,
  rows: [
    { id: 'p-1', email: 'sergio.montes@momentocapital.com', first_name: 'Sergio', last_name: 'Montes',
      keycloak_enabled: true, email_verified: true, organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external',
      role: 'Owner', active: true, joined_at: '2026-04-14T18:23:40Z',
      last_seen_at: '2026-09-01T21:33:16Z', studies_created: 24, tokens_spent: 33241 },
    { id: 'p-2', email: 'fedomg@gmail.com', first_name: 'Federico', last_name: 'Dominguez',
      keycloak_enabled: true, email_verified: true, organization_id: 'o-2', org_name: 'Edgebridge Capital', org_class: 'external',
      role: 'Owner', active: true, joined_at: '2026-09-01T17:53:21Z',
      last_seen_at: '2026-09-01T19:00:53Z', studies_created: 1, tokens_spent: 75 },
    { id: 'p-3', email: 'oscar.saldana@momentocapital.com', first_name: 'Oscar', last_name: 'Saldaña',
      keycloak_enabled: true, email_verified: true, organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external',
      role: 'Manager', active: true, joined_at: '2026-05-05T17:30:49Z',
      last_seen_at: '2026-07-29T19:51:01Z', studies_created: 1, tokens_spent: 12900 },
    // Sin correo en Keycloak: el colector nunca lo vio.
    { id: 'p-4', email: null, first_name: null, last_name: null,
      keycloak_enabled: null, email_verified: true, organization_id: 'o-3', org_name: 'ingjars', org_class: 'external',
      role: 'Owner', active: true, joined_at: '2026-05-20T19:57:14Z',
      last_seen_at: '2026-05-21T22:55:05Z', studies_created: 3, tokens_spent: 0 },
    // Sin rastro alguno.
    { id: 'p-5', email: 'ingjars@hotmail.com', first_name: 'jorge', last_name: 'rodriguez',
      keycloak_enabled: true, email_verified: false, organization_id: 'o-4', org_name: 'ShadowMouses', org_class: 'external',
      role: 'Owner', active: true, joined_at: '2026-08-28T20:51:53Z',
      last_seen_at: null, studies_created: 0, tokens_spent: 0 },
    // Miembro de la app, deshabilitado en Keycloak: no puede entrar.
    { id: 'p-6', email: 'exempleado@ejemplo.com', first_name: 'Ex', last_name: 'Empleado',
      keycloak_enabled: false, email_verified: true, organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external',
      role: 'Manager', active: true, joined_at: '2026-05-01T10:00:00Z',
      last_seen_at: '2026-06-15T10:00:00Z', studies_created: 0, tokens_spent: 0 },
    // Twelve more, so the role donut and the per-person histogram have a shape.
    // The roles are the ones the fixtures have always shown; this repo does not
    // know the real domain of `users.role`, so it invents none.
    // ONE PERSON PER MEMBER the org claims. Generating two while the roster
    // said eight made two panels on one screen describe the same population as
    // 36 people and as 21.
    ...EXTRA_ORGS.flatMap((o, i) =>
      Array.from({ length: o.members }, (_, k) => ({
        id: `p-${o.id}-${k}`,
        email: `persona${k + 1}@${o.slug}.example`,
        first_name: null, last_name: null,
        keycloak_enabled: true, email_verified: (i + k) % 3 !== 0,
        organization_id: o.id, org_name: o.name, org_class: 'external' as const,
        role: k === 0 ? 'Owner' : i % 2 === 0 ? 'Manager' : 'Admin',
        active: true, joined_at: '2026-06-15T00:00:00Z',
        last_seen_at: k === 0 ? '2026-08-30T12:00:00Z' : null,
        studies_created: (i * 3 + k) % 7,
        tokens_spent: Math.round((o.tokens * 0.62) / o.members),
      })),
    ),
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 18, sources: ['LEDGER', 'EXTERNAL'], tz: 'UTC' },
}

/** Canjes: el estado REAL de hoy — dos sesiones abandonadas, cero cupones. */
export const redemptions = {
  snapshot_at: '2026-09-02T17:16:15Z',
  sessions_row_limit: 200,
  sessions_truncated: false,
  n: 2, n_paid: 0, n_free: 0, n_expired: 2, n_with_code: 0,
  cash_usd: 0, discount_usd: 0, abandoned_usd: 100,
  codes_total: 0, codes_redeemed: 0,
  sessions: [
    { id: 'cs_live_b1etQ1', created_at: '2026-08-24T20:16:18Z', status: 'expired',
      payment_status: 'unpaid', amount_usd: 50, discount_usd: 0, promotion_code: null,
      org_name: 'Momento Capital', org_join_path: 'ledger_reference' },
    { id: 'cs_live_b1Gxh2', created_at: '2026-08-05T18:25:10Z', status: 'expired',
      payment_status: 'unpaid', amount_usd: 50, discount_usd: 0, promotion_code: null,
      org_name: 'Personal', org_join_path: 'ledger_reference' },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 6, sources: ['EXTERNAL'], tz: 'UTC' },
}

/** Almacenamiento: la concentración real — una org es el 89%. */
/**
 * Las dos fotografías nocturnas. Fechadas, y con la fila incómoda: un estudio
 * de mayo con detalle heredado que pesa cinco veces lo que uno de septiembre
 * con el doble de trials. Esa desproporción es la razón de la columna.
 */
export const heaviestStudies = {
  meta: meta(['MEASURED'], 4),
  rows: [
    { photo_day: '2026-09-11', study_id: 4812, organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external', trials: 1200, with_snapshot: 240, bytes_est: 16204200000, legacy_detail: true, study_updated_at: '2026-05-14T10:02:00Z' },
    { photo_day: '2026-09-11', study_id: 7731, organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external', trials: 2000, with_snapshot: 2000, bytes_est: 3316000000, legacy_detail: false, study_updated_at: '2026-09-01T21:33:16Z' },
    { photo_day: '2026-09-11', study_id: 3305, organization_id: 'o-2', org_name: 'Fintela', org_class: 'internal', trials: 800, with_snapshot: 800, bytes_est: 1326400000, legacy_detail: false, study_updated_at: '2026-07-19T09:41:00Z' },
  ],
}

export const heaviestTables = {
  meta: meta(['LEDGER'], 3),
  rows: [
    { photo_day: '2026-09-11', table_name: 'portfolio_snapshots', total_bytes: 214748364800, heap_bytes: 201863462912, index_bytes: 12884901888, row_estimate: 152313 },
    { photo_day: '2026-09-11', table_name: 'trial_values', total_bytes: 96636764160, heap_bytes: 85899345920, index_bytes: 10737418240, row_estimate: 48210000 },
    { photo_day: '2026-09-11', table_name: 'equity', total_bytes: 32212254720, heap_bytes: 28991029248, index_bytes: 3221225472, row_estimate: 9107000 },
    { photo_day: '2026-09-11', table_name: 'support_ticket_messages', total_bytes: 106496, heap_bytes: 65536, index_bytes: 40960, row_estimate: 14 },
  ],
}

export const storage = {
  rows: [
    { organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external', resource_kind: 'trial_snapshot',
      units: 142418, bytes_est: 198198003552, computed_at: '2026-09-02T06:30:42Z', storage_quota_bytes: 2199023255552 },
    { organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external', resource_kind: 'trial_detail_legacy',
      units: 9895, bytes_est: 171686067050, computed_at: '2026-09-02T06:30:42Z', storage_quota_bytes: 2199023255552 },
    { organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external', resource_kind: 'equity_curve',
      units: 152313, bytes_est: 18103770867, computed_at: '2026-09-02T06:30:42Z', storage_quota_bytes: 2199023255552 },
    // Sin cuota configurada: la barra tiene que decirlo, no asumir cero.
    { organization_id: 'o-2', org_name: 'ingjars', org_class: 'external', resource_kind: 'trial_detail_legacy',
      units: 50, bytes_est: 867539500, computed_at: '2026-09-02T06:30:42Z', storage_quota_bytes: null },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 5, sources: ['MEASURED'], tz: 'UTC' },
}

/** Actividad: created / updated / deleted, y una fila sin actor. */
export const activity = {
  is_complete: false,
  // The server cap, so the table can say what its sort actually covers.
  row_limit: 200,
  truncated: false,
  rows: [
    { id: 5877, created_at: '2026-09-01T21:33:16Z', event_type: 'study.created', entity_type: 'study',
      entity_name: 'short_overextended · Spy 2020', status: null, actor_id: 'a-1',
      actor_name: 'sergio.montes@momentocapital.com', organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external' },
    { id: 5876, created_at: '2026-09-01T21:28:15Z', event_type: 'data_cluster.created', entity_type: 'data_cluster',
      entity_name: 'prueba_6', status: null, actor_id: 'a-1',
      actor_name: 'sergio.montes@momentocapital.com', organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external' },
    { id: 5842, created_at: '2026-09-01T19:00:53Z', event_type: 'study.created', entity_type: 'study',
      entity_name: 'fdg_monthly_sortino · FDG', status: null, actor_id: 'a-2',
      actor_name: 'fdg', organization_id: 'o-2', org_name: 'Edgebridge Capital', org_class: 'external' },
    { id: 5700, created_at: '2026-08-30T10:00:00Z', event_type: 'strategy.deleted', entity_type: 'strategy',
      entity_name: 'vieja', status: null, actor_id: null, actor_name: null,
      organization_id: 'o-1', org_name: 'Momento Capital', org_class: 'external' },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 9, sources: ['LEDGER'], tz: 'UTC' },
}

/** Calibración: una fuente sin glosa, para probar que igual se renderiza. */
export const calibration = {
  rows: [
    { source: 'revenue_rates', key: 'token_usd_compute', value: 0.05, updated_at: '2026-08-31T00:00:00Z', changed_recently: true },
    { source: 'revenue_rates', key: 'token_usd_ai', value: 0.01, updated_at: '2026-08-31T00:00:00Z', changed_recently: true },
    { source: 'token_cost_config', key: 'optimization', value: 1, updated_at: '2026-09-02T00:00:00Z', changed_recently: true },
    { source: 'token_cost_config', key: 'backtest', value: 1, updated_at: '2026-06-17T00:00:00Z', changed_recently: false },
    { source: 'tabla_nueva_sin_glosa', key: 'algo', value: 42, updated_at: '2026-07-01T00:00:00Z', changed_recently: false },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 2, sources: ['LEDGER'], tz: 'UTC' },
}

/** Margen: la tasa de reembolso real de agosto, y una operación sin costo medido. */
export const operationMargin = {
  rows: [
    { operation: 'optimization', month: '2026-08-01', tokens_charged: 32612, tokens_refunded: 12272,
      tokens_net: 20340, list_value_usd: 1017.0, direct_cost_usd: 32.91, tasks: 186,
      contribution_margin: -32.91, refund_rate: 0.3763 },
    { operation: 'basket_update', month: '2026-08-01', tokens_charged: 1390, tokens_refunded: 0,
      tokens_net: 1390, list_value_usd: 69.5, direct_cost_usd: null, tasks: null,
      contribution_margin: null, refund_rate: 0 },
    { operation: 'sandbox_run', month: '2026-08-01', tokens_charged: 21, tokens_refunded: 0,
      tokens_net: 21, list_value_usd: 1.05, direct_cost_usd: null, tasks: null,
      contribution_margin: null, refund_rate: 0 },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 22, sources: ['LEDGER', 'MEASURED'], tz: 'UTC' },
}

/** Cómputo: los números reales — 9% de uso, 8 OOMs, modelo corto por 6.4x. */
export const computeEfficiency = {
  tasks_total: 340, tasks_measured: 239, ooms: 8,
  avg_alloc_mib: 50043.2, avg_peak_mib: 4889.0, avg_predicted_mib: 760.7,
  avg_utilisation: 0.08996, avg_cpu_units: 16126.9, avg_workers: 15.74,
  since: '2026-05-15T20:34:42Z', until: '2026-09-01T21:33:23Z',
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 14, sources: ['MEASURED'], tz: 'UTC' },
}

/** Detalle de organización: el cliente real, con sus bloques compuestos. */
export const orgDetail = {
  id: 'o-1', name: 'Momento Capital', slug: 'momento-capital', org_class: 'external',
  class_source: 'manual:2026-09-02', status: null, created_at: '2026-04-13T16:33:28Z',
  token_balance: 13096, ai_token_balance: 14801,
  tokens_depleted_at: null, disputed_at: null,
  spend_cap_tokens: 100000, storage_quota_bytes: 2199023255552,
  members: [
    { id: 'm-1', email: 'sergio.montes@momentocapital.com', role: 'Owner', active: true, joined_at: '2026-04-14T18:23:40Z' },
    { id: 'm-2', email: 'myrna.ortiz@momentocapital.com', role: 'Admin', active: true, joined_at: '2026-04-13T16:33:28Z' },
    // Sin correo, e inactivo: los dos casos borde en una fila.
    { id: 'm-3', email: null, role: 'Manager', active: false, joined_at: '2026-05-05T17:30:49Z' },
  ],
  studies_total: 67, studies_launched: 67, studies_completed: 62, trials_total: 161014,
  last_study_at: '2026-09-01T21:33:15Z',
  ledger_by_reason: [
    { reason: 'optimization', tokens: -185536, n: 161 },
    { reason: 'refund', tokens: 97713, n: 62 },
    { reason: 'basket_update', tokens: -1907, n: 588 },
  ],
  storage_by_kind: [
    { kind: 'trial_snapshot', bytes: 198198003552, units: 142418 },
    { kind: 'trial_detail_legacy', bytes: 171686067050, units: 9895 },
  ],
  storage_bytes: 402133759031,
  recent_activity_limit: 20,
  // Poco halagador a propósito: el más pesado arrastra detalle heredado y pesa
  // más que los otros nueve juntos. Es la fila que hace útil la columna.
  heaviest_studies_limit: 10,
  global_storage_quota_bytes: 107374182400,
  heaviest_studies: [
    { photo_day: '2026-09-11', study_id: 4812, trials: 1200, with_snapshot: 240, bytes_est: 16204200000, legacy_detail: true, study_updated_at: '2026-05-14T10:02:00Z' },
    { photo_day: '2026-09-11', study_id: 7731, trials: 2000, with_snapshot: 2000, bytes_est: 3316000000, legacy_detail: false, study_updated_at: '2026-09-01T21:33:16Z' },
    { photo_day: '2026-09-11', study_id: 7690, trials: 500, with_snapshot: 500, bytes_est: 829000000, legacy_detail: false, study_updated_at: '2026-08-28T15:10:00Z' },
  ],
  recent_activity: [
    { created_at: '2026-09-01T21:33:16Z', event_type: 'study.created', entity_name: 'short_overextended · Spy 2020', actor_name: 'sergio.montes@momentocapital.com' },
    { created_at: '2026-09-01T21:28:15Z', event_type: 'data_cluster.created', entity_name: 'prueba_6', actor_name: 'sergio.montes@momentocapital.com' },
  ],
  // En demo, y no es la primera: la anterior venció. La ficha tiene que decir
  // hasta cuándo y que ya hubo otra, sin que nadie abra la bitácora.
  demo_until: '2026-10-11T15:04:00Z',
  demo_grants: [
    { starts_at: '2026-09-11T15:04:00Z', ends_at: '2026-10-11T15:04:00Z', days: 30, reason: 'Demo acordada con el CIO el 10 de septiembre; van a probar Laboratorio.', active: true, reference_id: '6d1f6a2e-0c1b-4a1d-9a6c-1f7d3f8a2b10',
      tokens: 5_000, ai_tokens: 200, reclaimed_at: null, tokens_clawed: null, tokens_shortfall: null, ai_tokens_clawed: null, ai_tokens_shortfall: null },
    // Vencida y ya liquidada: trajo 2 000, gastaron 1 250, volvieron 750.
    { starts_at: '2026-06-01T09:00:00Z', ends_at: '2026-06-15T09:00:00Z', days: 14, reason: 'Primera demo, dos semanas, para la reunión de junio.', active: false, reference_id: null,
      tokens: 2_000, ai_tokens: 0, reclaimed_at: '2026-06-16T06:30:00Z', tokens_clawed: 750, tokens_shortfall: 0, ai_tokens_clawed: 0, ai_tokens_shortfall: 0 },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 31, sources: ['LEDGER', 'MEASURED'], tz: 'UTC' },
}

/** Proyecciones: 32 días de costo ruidoso (la banda cruza cero, así que la
 *  pantalla debe NEGARSE a dibujar pendiente) y sin saldo de caja. */
export const projectionInputs = {
  capacity_profile: 'seed',
  infra_floor_usd_month: 1637,
  rung_since: '2026-08-01T00:00:00Z',
  daily_cost: Array.from({ length: 32 }, (_, i) => ({
    day: `2026-08-${String((i % 31) + 1).padStart(2, '0')}`,
    usd: 95 + ((i * 7919) % 23) - 11,
  })),
  daily_points: 32,
  token_usd_compute: 0.05,
  token_usd_ai: 0.01,
  operation_costs: [
    { operation: 'optimization', base_cost: 1 },
    { operation: 'sandbox_run', base_cost: 1 },
    { operation: 'alloc_risk_parity', base_cost: 150 },
    { operation: 'storage_pack', base_cost: 25 },
  ],
  company_cash: [],
  cash_points: 0,
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 11, sources: ['MEASURED', 'LEDGER'], tz: 'UTC' },
}

/** Historia congelada: tres meses reales, con la caída de volumen y el salto de
 *  la tasa de reembolso que la vista de un solo mes no puede mostrar. */
export const marginHistory = {
  rows: [
    { month: '2026-06-01', operation: 'optimization', tokens_charged: 8580, tokens_refunded: 0,
      tokens_net: 8580, token_usd_at_freeze: 0.05, list_value_usd: 429, direct_cost_usd: null,
      frozen_at: '2026-07-01T07:00:00Z', refund_rate: 0 },
    { month: '2026-07-01', operation: 'optimization', tokens_charged: 143622, tokens_refunded: 19437,
      tokens_net: 124185, token_usd_at_freeze: 0.05, list_value_usd: 6209.25, direct_cost_usd: 45.03,
      frozen_at: '2026-08-01T07:00:00Z', refund_rate: 0.1353 },
    { month: '2026-08-01', operation: 'optimization', tokens_charged: 32612, tokens_refunded: 12272,
      tokens_net: 20340, token_usd_at_freeze: 0.05, list_value_usd: 1017, direct_cost_usd: 32.91,
      frozen_at: '2026-09-01T07:00:00Z', refund_rate: 0.3763 },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 3, sources: ['LEDGER', 'MEASURED'], tz: 'UTC' },
}

/** Vigilancia: los hallazgos REALES de hoy, más uno resuelto para probar que
 *  los resueltos se conservan en vez de desaparecer. */
export const watch = {
  row_limit: 200,
  truncated: false,
  rows: [
    { rule: 'refunds.high', subject: '2026-08', severity: 'error',
      detail: 'En 2026-08 se devolvió el 35.8% de los tokens cobrados.', value: 0.3581,
      first_seen_at: '2026-09-01T07:00:00Z', last_seen_at: '2026-09-02T22:00:00Z', resolved_at: null },
    { rule: 'org.stalled', subject: 'Manuel Personal', severity: 'warn',
      detail: 'Manuel Personal se registró hace 109 días y nunca ha creado un estudio.', value: 109,
      first_seen_at: '2026-06-01T07:00:00Z', last_seen_at: '2026-09-02T22:00:00Z', resolved_at: null },
    { rule: 'compute.overprovisioned', subject: 'flota', severity: 'warn',
      detail: 'Las tareas usan en promedio el 9% de la memoria que reservan; en Fargate se paga lo reservado.',
      value: 0.09, first_seen_at: '2026-08-15T07:00:00Z', last_seen_at: '2026-09-02T22:00:00Z', resolved_at: null },
    { rule: 'orgs.unclassified', subject: 'todas', severity: 'warn',
      detail: '24 organizaciones siguen sin clasificar; los agregados de clientes las excluyen.',
      value: 24, first_seen_at: '2026-09-01T07:00:00Z', last_seen_at: '2026-09-02T05:00:00Z',
      resolved_at: '2026-09-02T06:00:00Z' },
  ],
  meta: { generated_at: '2026-09-02T00:00:00Z', query_ms: 4, sources: ['LEDGER'], tz: 'UTC' },
}

/**
 * `/incidents` — la cola de triaje.
 *
 * Trae a propósito los casos FEOS, porque un fixture de casos bonitos enseña la
 * pantalla equivocada: una ausencia (que no produce error en ningún otro lado),
 * un patrón que agrupa 312 ocurrencias en UN renglón, una culpa «sin determinar»
 * que nunca se dobla en «del usuario», y una reapertura — que es el número más
 * útil de la tabla.
 */
export const incidents = {
  truncated: false,
  counts: { total: 5, errors: 2, ours: 3, absences: 2, acknowledged: 1 },
  rows: [
    { incident_id: 41, detector: 'collector.stale', dedupe_key: 'stripe-sync', shape: 'condition',
      category: 'schedule_absence', blame: 'fintela', blame_basis: 'detector_constant',
      severity: 'error', scope: 'platform', subject_kind: 'job', subject_label: 'stripe-sync',
      organization_id: null, detail: 'El colector stripe-sync no termina bien desde hace 61 horas.',
      value: '61.0', priority: 88, is_absence: true, status: 'open', occurrences_total: 12,
      occurrences_24h: 12, orgs_affected_7d: 0, first_seen_at: '2026-09-05T06:15:00Z',
      last_seen_at: '2026-09-07T12:15:00Z', resolved_at: null, resolution: null, reopen_count: 0,
      assignee_id: null, acknowledged_at: null, snoozed_until: null,
      diagnostic_kind: null, diagnostic_stage: null },
    { incident_id: 44, detector: 'study.failed_platform', dedupe_key: 'optimize|OUT_OF_MEMORY',
      shape: 'pattern', category: 'study_failure', blame: 'fintela',
      blame_basis: 'diagnostic_rank_platform', severity: 'warn', scope: 'platform',
      subject_kind: 'kind', subject_label: 'OUT_OF_MEMORY', organization_id: null,
      detail: '312 estudios de 4 organización(es) fallaron en optimize con OUT_OF_MEMORY en los últimos 7 días.',
      value: '312', priority: 71, is_absence: false, status: 'acknowledged', occurrences_total: 84,
      occurrences_24h: 9, orgs_affected_7d: 4, first_seen_at: '2026-08-30T02:15:00Z',
      last_seen_at: '2026-09-07T12:15:00Z', resolved_at: null, resolution: null, reopen_count: 1,
      assignee_id: '00000000-0000-0000-0000-000000000001',
      acknowledged_at: '2026-09-06T10:00:00Z', snoozed_until: null,
      diagnostic_kind: 'OUT_OF_MEMORY', diagnostic_stage: 'optimize' },
    { incident_id: 47, detector: 'task.heartbeat_dead', dedupe_key: 'flota', shape: 'condition',
      category: 'task_failure', blame: 'fintela', blame_basis: 'detector_constant',
      severity: 'error', scope: 'platform', subject_kind: 'task', subject_label: 'flota',
      organization_id: null,
      detail: '7 tareas siguen en RUNNING con el latido detenido; la más vieja lleva 9.4 horas.',
      value: '9.4', priority: 66, is_absence: true, status: 'open', occurrences_total: 4,
      occurrences_24h: 4, orgs_affected_7d: 0, first_seen_at: '2026-09-07T04:15:00Z',
      last_seen_at: '2026-09-07T12:15:00Z', resolved_at: null, resolution: null, reopen_count: 0,
      assignee_id: null, acknowledged_at: null, snoozed_until: null,
      diagnostic_kind: null, diagnostic_stage: null },
    { incident_id: 52, detector: 'refunds.high', dedupe_key: '2026-08', shape: 'condition',
      category: 'money_risk', blame: 'unknown', blame_basis: 'detector_constant',
      severity: 'warn', scope: 'platform', subject_kind: 'month', subject_label: '2026-08',
      organization_id: null, detail: 'En 2026-08 se devolvió el 35.8% de los tokens cobrados.',
      value: '0.3581', priority: 40, is_absence: false, status: 'open', occurrences_total: 31,
      occurrences_24h: 12, orgs_affected_7d: 0, first_seen_at: '2026-09-01T07:00:00Z',
      last_seen_at: '2026-09-07T12:15:00Z', resolved_at: null, resolution: null, reopen_count: 0,
      assignee_id: null, acknowledged_at: null, snoozed_until: null,
      diagnostic_kind: null, diagnostic_stage: null },
    { incident_id: 58, detector: 'org.stalled', dedupe_key: 'Manuel Personal', shape: 'condition',
      category: 'customer_health', blame: 'unknown', blame_basis: 'detector_constant',
      severity: 'warn', scope: 'org', subject_kind: 'organization',
      subject_label: 'Manuel Personal', organization_id: '00000000-0000-0000-0000-0000000000ab',
      detail: 'Manuel Personal se registró hace 109 días y nunca ha creado un estudio.',
      value: '109', priority: 22, is_absence: false, status: 'open', occurrences_total: 96,
      occurrences_24h: 12, orgs_affected_7d: 1, first_seen_at: '2026-06-01T07:00:00Z',
      last_seen_at: '2026-09-07T12:15:00Z', resolved_at: null, resolution: null, reopen_count: 0,
      assignee_id: null, acknowledged_at: null, snoozed_until: null,
      diagnostic_kind: null, diagnostic_stage: null },
  ],
  meta: { generated_at: '2026-09-07T12:20:00Z', query_ms: 6, sources: ['LEDGER'], tz: 'UTC' },
}

/** `/incidents/:id` — el detalle, con su hilo. */
export const incidentDetail = {
  ...incidents.rows[1],
  acknowledged_by: '00000000-0000-0000-0000-000000000001',
  evidence: { stage: 'optimize', kind: 'OUT_OF_MEMORY', peak_memory_mib: 15873, memory_mib: 16384 },
  money_at_risk_usd: null,
  orgs_affected: 4,
  notify: false,
  thread: [
    { id: 3, occurred_at: '2026-09-06T10:00:00Z', kind: 'acknowledged', from_status: 'open',
      to_status: 'acknowledged', actor_id: '00000000-0000-0000-0000-000000000001', by_engine: false,
      detail: 'Subimos la memoria de la familia grande; queda ver si baja mañana.' },
    { id: 2, occurred_at: '2026-09-02T02:15:00Z', kind: 'reopened', from_status: 'resolved',
      to_status: 'open', actor_id: null, by_engine: true,
      detail: 'Volvió a dispararse tras haberse dado por cerrado. Reaperturas: 1.' },
    { id: 1, occurred_at: '2026-08-30T02:15:00Z', kind: 'opened', from_status: null,
      to_status: 'open', actor_id: null, by_engine: true,
      detail: 'El detector study.failed_platform lo levantó por primera vez.' },
  ],
  buckets: [
    { bucket_start: '2026-09-07T12:00:00Z', occurrences: 1, max_value: '312',
      subjects: ['OUT_OF_MEMORY'] },
    { bucket_start: '2026-09-07T10:00:00Z', occurrences: 1, max_value: '298',
      subjects: ['OUT_OF_MEMORY'] },
  ],
  siblings: [
    { incident_id: 45, dedupe_key: 'data_loading|MISSING_SOURCE_DATA', subject_label: 'MISSING_SOURCE_DATA',
      severity: 'warn', priority: 33, status: 'open' },
  ],
  meta: { generated_at: '2026-09-07T12:20:00Z', query_ms: 8, sources: ['LEDGER'], tz: 'UTC' },
}

/**
 * `/metrics/orgs` — el mismo censo que `orgRoster`, con el consumo del rollup.
 *
 * Los números están alineados a mano con las otras fixtures a propósito: una
 * fixture que no coincide con sus vecinas no prueba la pantalla, prueba otra
 * pantalla. `Northgate` va con el rollup SIN cubrir (`days_present: 0`) porque
 * el estado de "sin medir" es la mitad del diseño de este endpoint y no hay
 * otra forma de verlo sin romper producción.
 */
/**
 * `/metrics/orgs` — el mismo censo que `orgRoster`, con el consumo del rollup.
 *
 * Los números están alineados a mano con las otras fixtures a propósito: una
 * fixture que no coincide con sus vecinas no prueba la pantalla, prueba otra
 * pantalla. `Northgate` va con el rollup SIN cubrir (`days_present: 0`) porque
 * el estado "sin medir" es la mitad del diseño de este endpoint y no hay otra
 * forma de verlo sin romper producción.
 *
 * La serie diaria se GENERA densa, con dos días sin cobertura, y
 * `rollup_days_present` se deriva de ella — no se teclea. Un número tecleado
 * ahí puede contradecir al arreglo que tiene al lado, y entonces la fixture
 * describe una respuesta que el servidor no puede producir.
 */
const SPINE_DAYS = 90
const UNCOVERED = new Set(['2026-08-30', '2026-08-31'])

/**
 * The activity measurement starts PART WAY THROUGH the window, on purpose.
 *
 * The rollup wrote `activity_events` as a literal zero until it was taught to
 * measure, so every day before that carries a row with a null in it — a state
 * `rollup_ran` cannot express, because the rollup DID run. Fixture mode is the
 * only place anyone can see the two gaps side by side without waiting for
 * production to accumulate them, and a fixture where everything is measured
 * would hide the exact case the screens were written for.
 */
const ACTIVITY_FROM = 60

const dailySpine: DailyPoint[] = Array.from({ length: SPINE_DAYS }, (_, k) => {
  const day = new Date(Date.UTC(2026, 8, 1) - (SPINE_DAYS - 1 - k) * 86_400_000)
    .toISOString()
    .slice(0, 10)
  if (UNCOVERED.has(day)) {
    return { day, rollup_ran: false, compute_tokens_spent: null, ai_tokens_spent: null,
             tokens_credited: null, orgs_active: null, activity_events: null,
             actor_orgs_active: null }
  }
  const activityMeasured = k >= ACTIVITY_FROM
  // Un patrón determinista, no aleatorio: una fixture que cambia entre
  // ejecuciones no se puede comparar contra una captura de pantalla anterior.
  return {
    day,
    rollup_ran: true,
    compute_tokens_spent: 900 + ((k * 137) % 2600),
    ai_tokens_spent: 40 + ((k * 31) % 180),
    tokens_credited: k % 30 === 0 ? 20_000 : 0,
    orgs_active: 2 + (k % 3),
    activity_events: activityMeasured ? 40 + ((k * 17) % 220) : null,
    actor_orgs_active: activityMeasured ? 3 + (k % 5) : null,
  }
})

/** Covered days that also carry the activity measurement. */
const ACTIVITY_DAYS = dailySpine.filter(
  (d) => d.rollup_ran && d.activity_events !== null,
).length

/** Derived once, so no row can disagree with the spine beside it. */
const COVERED_DAYS = dailySpine.filter((d) => d.rollup_ran).length

export const orgMetrics: OrgMetrics = {
  meta: meta(['LEDGER', 'MEASURED'], 41),
  bytes_as_of: '2026-09-01',
  rollup_days_present: COVERED_DAYS,
  window_days: SPINE_DAYS,
  platform_storage_capacity_known: false,
  daily: dailySpine,
  rows: [
    // `external`, matching every other fixture. It was `internal` here and
    // `external` in five others, and the dashboard is the first screen to
    // render both at once — the roster would have called it a customer while
    // the class donut counted it as internal.
    { organization_id: '1', org_name: 'Momento Capital', slug: 'momento-capital', org_class: 'external',
      created_at: '2026-04-11T00:00:00Z', members_active: 6, members_all: 7,
      token_balance: 182_400, ai_token_balance: 12_800, storage_quota_bytes: 2_199_023_255_552,
      compute_tokens_spent: 141_200, ai_tokens_spent: 8_940, tokens_credited: 320_000,
      days_present: COVERED_DAYS, last_rollup_day: '2026-09-01', bytes_est: 232_000_000_000, bytes_as_of: '2026-09-01',
      activity_events: 4_180, studies_created: 62, actors_active_peak: 5, activity_days_measured: ACTIVITY_DAYS },
    { organization_id: '2', org_name: 'Fintela', slug: 'fintela', org_class: 'internal',
      created_at: '2026-04-11T00:00:00Z', members_active: 4, members_all: 4,
      token_balance: 96_100, ai_token_balance: 8_400, storage_quota_bytes: null,
      compute_tokens_spent: 61_050, ai_tokens_spent: 3_120, tokens_credited: 160_000,
      days_present: COVERED_DAYS, last_rollup_day: '2026-09-01', bytes_est: 151_000_000_000, bytes_as_of: '2026-09-01',
      activity_events: 2_940, studies_created: 31, actors_active_peak: 4, activity_days_measured: ACTIVITY_DAYS },
    { organization_id: '3', org_name: 'EdgeBridge Capital', slug: 'edgebridge', org_class: 'external',
      created_at: '2026-06-02T00:00:00Z', members_active: 3, members_all: 3,
      token_balance: 41_250, ai_token_balance: 1_900, storage_quota_bytes: 107_374_182_400,
      compute_tokens_spent: 18_700, ai_tokens_spent: 1_240, tokens_credited: 60_000,
      days_present: COVERED_DAYS, last_rollup_day: '2026-09-01', bytes_est: 640_000_000, bytes_as_of: '2026-09-01',
      activity_events: 610, studies_created: 8, actors_active_peak: 3, activity_days_measured: ACTIVITY_DAYS },
    // El rollup nunca cubrió a esta: `sin medir`, que no es cero.
    { organization_id: '4', org_name: 'Northgate Research', slug: 'northgate', org_class: 'external',
      created_at: '2026-07-18T00:00:00Z', members_active: 2, members_all: 2,
      token_balance: 380, ai_token_balance: 0, storage_quota_bytes: 107_374_182_400,
      compute_tokens_spent: null, ai_tokens_spent: null, tokens_credited: null,
      days_present: 0, last_rollup_day: null, bytes_est: null, bytes_as_of: '2026-09-01',
      activity_events: null, studies_created: null, actors_active_peak: null, activity_days_measured: 0 },
    { organization_id: '5', org_name: 'test_qa_sandbox', slug: 'test-qa', org_class: 'test',
      created_at: '2026-05-20T00:00:00Z', members_active: 1, members_all: 1,
      token_balance: 400, ai_token_balance: 200, storage_quota_bytes: null,
      compute_tokens_spent: 0, ai_tokens_spent: 0, tokens_credited: 600,
      days_present: COVERED_DAYS, last_rollup_day: '2026-09-01', bytes_est: 2_000_000, bytes_as_of: '2026-09-01',
      activity_events: 0, studies_created: 0, actors_active_peak: 0, activity_days_measured: ACTIVITY_DAYS },
    { organization_id: '6', org_name: 'Calder & Voss', slug: 'calder-voss', org_class: 'unknown',
      created_at: '2026-08-29T00:00:00Z', members_active: 1, members_all: 1,
      token_balance: 400, ai_token_balance: 200, storage_quota_bytes: null,
      compute_tokens_spent: 210, ai_tokens_spent: 60, tokens_credited: 600,
      days_present: 4, last_rollup_day: '2026-09-01', bytes_est: 1_100_000, bytes_as_of: '2026-09-01',
      activity_events: 12, studies_created: 1, actors_active_peak: 1, activity_days_measured: 4 },
    ...EXTRA_ORGS.map((o) => ({
      organization_id: o.id, org_name: o.name, slug: o.slug, org_class: 'external' as const,
      created_at: '2026-06-15T00:00:00Z',
      members_active: o.members, members_all: o.members + (o.members > 3 ? 1 : 0),
      token_balance: o.tokens, ai_token_balance: o.ai,
      storage_quota_bytes: o.members > 3 ? 107_374_182_400 : null,
      // Consumption is a fraction of the balance, so the scatter has spread
      // rather than a straight line.
      compute_tokens_spent: Math.round(o.tokens * 0.62),
      ai_tokens_spent: Math.round(o.ai * 0.71),
      tokens_credited: o.tokens,
      // Actividad proporcional al tamaño, y los estudios a los tokens: el
      // panel de distribución necesita masa con forma, no ruido.
      activity_events: o.members * 90 + (Number(o.id) % 7) * 30,
      studies_created: Math.round(o.tokens / 2_500),
      actors_active_peak: Math.min(o.members, 1 + (Number(o.id) % 4)),
      activity_days_measured: ACTIVITY_DAYS,
      // Derived from the spine, never typed: a hand-written count that
      // contradicts the array beside it is the exact defect this file's header
      // swears it avoids.
      days_present: COVERED_DAYS, last_rollup_day: '2026-09-01',
      bytes_est: o.bytes, bytes_as_of: '2026-09-01',
    })),
  ],
}

/**
 * `/metrics/money` — el efectivo real de hoy: CERO.
 *
 * Las dos sesiones que existen expiraron sin pagarse, así que `cash_usd` es 0
 * en todas las filas y `abandoned_usd` no lo es. La fila residual —
 * `organization_id: null` — es la sesión que el colector no pudo atribuir a
 * ninguna organización; NO lleva `org_class: 'unknown'`, porque esa es una
 * clase real que significa "todavía sin clasificar".
 */
export const orgMoney: OrgMoney = {
  meta: meta(['EXTERNAL'], 7),
  rows: [
    { organization_id: '3', org_name: 'EdgeBridge Capital', org_class: 'external', residual: null,
      sessions: 1, cash_usd: 0, discount_usd: 0, abandoned_usd: 50,
      ledger_purchases_missing_amount_compute: 4, ledger_purchases_missing_amount_ai: 0,
      snapshot_at: '2026-09-02T17:16:15Z' },
    { organization_id: '4', org_name: 'Northgate Research', org_class: 'external', residual: null,
      sessions: 0, cash_usd: 0, discount_usd: 0, abandoned_usd: 0,
      ledger_purchases_missing_amount_compute: 4, ledger_purchases_missing_amount_ai: 0,
      snapshot_at: '2026-09-02T17:16:15Z' },
    // Sin atribuir: la sesión que el colector no pudo ligar a ninguna
    // organización. NO lleva `org_class: 'unknown'` — esa es una clase real que
    // significa "todavía sin clasificar".
    { organization_id: null, org_name: null, org_class: null, residual: 'sin_atribuir',
      sessions: 1, cash_usd: 0, discount_usd: 0, abandoned_usd: 50,
      ledger_purchases_missing_amount_compute: 4, ledger_purchases_missing_amount_ai: 0,
      snapshot_at: '2026-09-02T17:16:15Z' },
    // Atribuida perfectamente, a una organización interna que el filtro de
    // clase excluyó. Es la otra mitad, y llamarla "sin atribuir" sería falso.
    { organization_id: null, org_name: null, org_class: null, residual: 'fuera_de_clase',
      sessions: 0, cash_usd: 0, discount_usd: 0, abandoned_usd: 0,
      ledger_purchases_missing_amount_compute: 4, ledger_purchases_missing_amount_ai: 0,
      snapshot_at: '2026-09-02T17:16:15Z' },
  ],
}

// ── Declarado al final a propósito ──────────────────────────────────────────
// Sus cifras se DERIVAN de `orgMetrics` y `people`, que están arriba: la tira
// de resumen se dibuja justo encima de paneles hechos con esas mismas filas, y
// una cifra tecleada aquí hacía que una sola pantalla afirmara dos conteos
// distintos de lo mismo.
/** Las claves REALES que emite el servidor. La versión anterior de esta fixture
 *  usaba nombres inventados antes de que existiera sql/platform/summary.sql, así
 *  que la pantalla de inversionistas —que busca `spent_30d`— no encontraba nada
 *  y silenciosamente omitía su tile. Una fixture que no coincide con el servidor
 *  no prueba la pantalla: prueba otra pantalla. */
export const platformSummary: PlatformSummary = {
  meta: meta(['LEDGER', 'MEASURED'], 120),
  // ── DERIVED, NOT TYPED ───────────────────────────────────────────────────
  // The dashboard puts this strip directly above panels drawn from
  // `orgMetrics` and `people`, over the same population. Typed figures drifted
  // from them, so one screen stated two different counts of the same thing —
  // which is exactly the class of defect fixtures are supposed to expose.
  stats: [
    {
      key: 'orgs_counted',
      label: 'Organizaciones',
      value: orgMetrics.rows.filter((r) => r.org_class === 'external').length,
      unit: 'count', provenance: 'LEDGER', delta: null,
      // NOT «internas»: folding `test` and `unknown` into that word is the
      // catch-all the house rules forbid.
      note: `${orgMetrics.rows.filter((r) => r.org_class !== 'external').length} no externas excluidas`,
    },
    { key: 'orgs_unknown', label: 'Sin clasificar', value: 0, unit: 'count', provenance: 'LEDGER', delta: null, note: 'El colector de Stripe aún no ha clasificado' },
    { key: 'users_active', label: 'Usuarios activos', value: people.rows.filter((p) => p.active).length, unit: 'count', provenance: 'LEDGER', delta: null, note: 'Definición por fila de usuario, no por inicio de sesión' },
    { key: 'spent_30d', label: 'Tokens consumidos · 30 d', value: 33_291, unit: 'tokens', provenance: 'LEDGER', delta: -0.691 },
    { key: 'tokens_outstanding', label: 'Saldo de tokens vigente', value: 14_633, unit: 'tokens', provenance: 'LEDGER', delta: null, note: 'Tokens, no dólares' },
    {
      key: 'bytes_counted', label: 'Almacenamiento', unit: 'bytes', provenance: 'MEASURED', delta: null,
      value: orgMetrics.rows
        .filter((r) => r.org_class === 'external')
        .reduce((a, r) => a + (r.bytes_est ?? 0), 0),
      note: 'Estimado por coeficiente; el orden es exacto',
    },
  ],
}

// ── comunicaciones ──────────────────────────────────────────────────────────
//
// Como el resto de este archivo, NO son cifras agradables inventadas. Están
// hechas para que las pantallas se revisen contra los estados que importan y no
// contra el feliz: hay un envío a medias con un rebote duro, un aviso in-app con
// lecturas, un contacto sin verificar, uno suprimido y uno interno. Si un panel
// se ve mal contra estos datos, probablemente el panel tenga razón.

export const commsList = {
  rows: [
    {
      id: 'c-1', kind: 'newsletter', category: 'marketing', channel: 'email',
      status: 'partially_sent', title: 'Lo nuevo de agosto', default_locale: 'es',
      segment_key: 'all_users', segment_name: 'All registered users',
      scheduled_at: '2026-08-28T16:00:00Z', created_at: '2026-08-27T11:02:00Z',
      created_by: 'u-1', approved_by: 'u-2', self_approved: false,
      estimated_recipients: 312, recipient_cap: 500, recipient_count: 312,
      send_started_at: '2026-08-28T16:00:11Z', send_finished_at: '2026-08-28T16:41:02Z',
    },
    {
      id: 'c-2', kind: 'announcement', category: 'marketing', channel: 'in_app',
      status: 'sent', title: 'Portfolio Manager cambió de lugar', default_locale: 'es',
      segment_key: 'all_users', segment_name: 'All registered users',
      scheduled_at: '2026-09-01T15:00:00Z', created_at: '2026-09-01T09:14:00Z',
      created_by: 'u-2', approved_by: 'u-1', self_approved: false,
      estimated_recipients: 24, recipient_cap: 50, recipient_count: 24,
      send_started_at: '2026-09-01T15:00:04Z', send_finished_at: '2026-09-01T15:00:19Z',
    },
    {
      id: 'c-3', kind: 'product_update', category: 'marketing', channel: 'email',
      status: 'pending_approval', title: 'Cierre de septiembre', default_locale: 'es',
      segment_key: 'internal_only', segment_name: 'Fintela staff only',
      scheduled_at: null, created_at: '2026-09-02T08:30:00Z',
      created_by: 'u-1', approved_by: null, self_approved: false,
      estimated_recipients: 6, recipient_cap: 50, recipient_count: null,
      send_started_at: null, send_finished_at: null,
    },
    {
      id: 'c-4', kind: 'newsletter', category: 'marketing', channel: 'email',
      status: 'draft', title: 'Sin título todavía', default_locale: 'es',
      segment_key: 'internal_only', segment_name: 'Fintela staff only',
      scheduled_at: null, created_at: '2026-09-02T17:45:00Z',
      created_by: 'u-2', approved_by: null, self_approved: false,
      estimated_recipients: null, recipient_cap: 50, recipient_count: null,
      send_started_at: null, send_finished_at: null,
    },
  ],
  meta: meta(['LEDGER']),
}

/** El de a medias: el estado que un panel escrito para el feliz pinta como éxito. */
export const commsDetail = {
  ...commsList.rows[0],
  variables: { app_url: 'https://app.fintela.io' },
  payload: {},
  submitted_at: '2026-08-27T14:00:00Z', submitted_by: 'u-1',
  approved_at: '2026-08-27T15:10:00Z',
  cancelled_at: null, cancelled_by: null, cancel_reason: null,
  audience_snapshot_at: '2026-08-28T16:00:05Z',
  webinar_id: null, webinar_role: null,
  pending: 0, sending: 0, sent: 298, failed: 5, skipped: 2, suppressed: 7,
  read_count: 0, delivered: 271, hard_bounced: 5, complained: 1, unsubscribed: 3,
  last_error: '550 5.1.1 The email account that you tried to reach does not exist',
  meta: meta(['LEDGER'], 22),
}

/**
 * El mismo detalle, pero en BORRADOR — que es el único estado que el editor
 * puede abrir.
 *
 * Sin esto, en modo fixture `/communications/c-4/edit` traía la fila
 * `partially_sent` de arriba, y el camino de edición —el que más lógica nueva
 * tiene— era el único que no se podía mirar sin credenciales.
 */
export const commsDraft = {
  ...commsDetail,
  ...commsList.rows[3],
  submitted_at: null, submitted_by: null,
  approved_at: null, approved_by: null,
  audience_snapshot_at: null,
  pending: 0, sending: 0, sent: 0, failed: 0, skipped: 0, suppressed: 0,
  read_count: 0, delivered: 0, hard_bounced: 0, complained: 0, unsubscribed: 0,
  last_error: null,
  meta: meta(['LEDGER'], 18),
}

export const commsContents = {
  rows: [
    {
      locale: 'es',
      subject_template: 'Hola {{ first_name }}, esto cambió en agosto',
      preheader: 'Estudios más rápidos y un panel nuevo.',
      body_markdown:
        '## Lo nuevo de agosto\n\nHola **{{ first_name }}**, esto cambió para {{ org_name }}:\n\n' +
        '- Los estudios corren ~30% más rápido\n- Nuevo panel de Portfolio Manager\n\n' +
        'Entra a [la aplicación]({{ app_url }}).',
    },
  ],
  meta: meta(['LEDGER'], 8),
}

export const commsRecipients = {
  rows: [
    {
      id: 'r-1', email: 'ana@ejemplo.com', normalized_email: 'ana@ejemplo.com',
      locale: 'es', status: 'failed', attempts: 3,
      last_error: '550 5.1.1 The email account that you tried to reach does not exist',
      skip_reason: null, sent_at: null, provider_message_id: null,
      keycloak_user_id: 'u-9', organization_id: 'o-1', notification_id: null,
      notification_read_at: null,
    },
    {
      id: 'r-2', email: 'beto@ejemplo.com', normalized_email: 'beto@ejemplo.com',
      locale: 'es', status: 'suppressed', attempts: 0, last_error: null,
      skip_reason: 'the recipient unsubscribed or was suppressed after this send began',
      sent_at: null, provider_message_id: null, keycloak_user_id: null,
      organization_id: null, notification_id: null, notification_read_at: null,
    },
    {
      id: 'r-3', email: 'carla@fintela.io', normalized_email: 'carla@fintela.io',
      locale: 'es', status: 'skipped', attempts: 0, last_error: null,
      skip_reason: 'outside COMMS_RECIPIENT_ALLOWLIST', sent_at: null,
      provider_message_id: null, keycloak_user_id: 'u-3', organization_id: 'o-0',
      notification_id: null, notification_read_at: null,
    },
    {
      id: 'r-4', email: 'diego@ejemplo.com', normalized_email: 'diego@ejemplo.com',
      locale: 'es', status: 'sent', attempts: 1, last_error: null, skip_reason: null,
      sent_at: '2026-08-28T16:03:44Z', provider_message_id: '0100019...-a1b2',
      keycloak_user_id: 'u-7', organization_id: 'o-2', notification_id: null,
      notification_read_at: null,
    },
  ],
  meta: meta(['LEDGER'], 14),
}

export const commsAudit = {
  rows: [
    { occurred_at: '2026-08-28T16:00:00Z', actor_id: 'u-2', actor_email: 'sergio@fintela.io',
      action: 'scheduled', from_status: 'approved', to_status: 'scheduled', detail: {} },
    { occurred_at: '2026-08-27T15:10:00Z', actor_id: 'u-2', actor_email: 'sergio@fintela.io',
      action: 'approved', from_status: 'pending_approval', to_status: 'approved',
      detail: { expected_recipients: 312 } },
    { occurred_at: '2026-08-27T14:00:00Z', actor_id: 'u-1', actor_email: 'ana@fintela.io',
      action: 'submitted', from_status: 'draft', to_status: 'pending_approval', detail: {} },
    { occurred_at: '2026-08-27T11:02:00Z', actor_id: 'u-1', actor_email: 'ana@fintela.io',
      action: 'created', from_status: null, to_status: 'draft',
      detail: { title: 'Lo nuevo de agosto', kind: 'newsletter', segment: 'all_users' } },
  ],
  meta: meta(['LEDGER'], 6),
}

export const commsSegments = {
  rows: [
    { id: 's-1', key: 'internal_only', name: 'Fintela staff only', kind: 'internal_only',
      params: {}, include_internal: true, is_builtin: true,
      created_at: '2026-08-15T00:00:00Z', created_by: null },
    { id: 's-2', key: 'all_users', name: 'All registered users', kind: 'all_users',
      params: {}, include_internal: false, is_builtin: true,
      created_at: '2026-08-15T00:00:00Z', created_by: null },
    // Existe y se ve normal, y ésa es exactamente la trampa que la pantalla
    // tiene que advertir: `last_seen_at` no lo escribe nadie.
    { id: 's-3', key: 'active_90d', name: 'Activos (90 días)', kind: 'active_users',
      params: { days: 90 }, include_internal: false, is_builtin: false,
      created_at: '2026-08-20T10:00:00Z', created_by: 'u-1' },
  ],
  meta: meta(['LEDGER'], 5),
}

export const commsContacts = {
  rows: [
    { id: 'ct-1', keycloak_user_id: 'u-7', email: 'diego@ejemplo.com',
      normalized_email: 'diego@ejemplo.com', display_name: 'Diego Ruiz', locale: 'es',
      organization_id: 'o-2', org_name: 'Edgebridge Capital', org_role: 'Analyst',
      source: 'keycloak_sync', keycloak_enabled: true, email_verified: true,
      is_internal: false, first_seen_at: '2026-06-02T10:00:00Z',
      last_seen_at: '2026-09-01T10:00:00Z', last_synced_at: '2026-09-02T00:00:00Z',
      deleted_at: null, suppression_scope: null, suppression_reason: null },
    { id: 'ct-2', keycloak_user_id: 'u-8', email: 'beto@ejemplo.com',
      normalized_email: 'beto@ejemplo.com', display_name: 'Beto Salas', locale: 'es',
      organization_id: 'o-1', org_name: 'Momento Capital', org_role: 'Owner',
      source: 'keycloak_sync', keycloak_enabled: true, email_verified: true,
      is_internal: false, first_seen_at: '2026-05-11T10:00:00Z',
      last_seen_at: '2026-08-29T10:00:00Z', last_synced_at: '2026-09-02T00:00:00Z',
      deleted_at: null, suppression_scope: 'marketing', suppression_reason: 'manual_unsubscribe' },
    { id: 'ct-3', keycloak_user_id: 'u-9', email: 'sinverificar@ejemplo.com',
      normalized_email: 'sinverificar@ejemplo.com', display_name: null, locale: 'en',
      organization_id: null, org_name: null, org_role: null,
      source: 'keycloak_sync', keycloak_enabled: true, email_verified: false,
      is_internal: false, first_seen_at: '2026-08-31T10:00:00Z',
      last_seen_at: '2026-08-31T10:00:00Z', last_synced_at: '2026-09-02T00:00:00Z',
      deleted_at: null, suppression_scope: null, suppression_reason: null },
    { id: 'ct-4', keycloak_user_id: 'u-3', email: 'carla@fintela.io',
      normalized_email: 'carla@fintela.io', display_name: 'Carla', locale: 'es',
      organization_id: 'o-0', org_name: 'Fintela', org_role: 'Owner',
      source: 'keycloak_sync', keycloak_enabled: true, email_verified: true,
      is_internal: true, first_seen_at: '2026-03-01T10:00:00Z',
      last_seen_at: '2026-09-02T09:00:00Z', last_synced_at: '2026-09-02T00:00:00Z',
      deleted_at: null, suppression_scope: null, suppression_reason: null },
  ],
  meta: meta(['LEDGER'], 19),
}

export const commsSuppressions = {
  rows: [
    { normalized_email: 'beto@ejemplo.com', email_raw: 'Beto@Ejemplo.com',
      scope: 'marketing', reason: 'manual_unsubscribe', source_comm_id: 'c-1',
      created_at: '2026-08-28T16:22:00Z', expires_at: null },
    { normalized_email: 'ana@ejemplo.com', email_raw: 'ana@ejemplo.com',
      scope: 'all', reason: 'hard_bounce', source_comm_id: 'c-1',
      created_at: '2026-08-28T16:05:00Z', expires_at: null },
    { normalized_email: 'temporal@ejemplo.com', email_raw: null,
      scope: 'all', reason: 'soft_bounce_threshold', source_comm_id: null,
      created_at: '2026-08-20T09:00:00Z', expires_at: '2026-09-19T09:00:00Z' },
  ],
  meta: meta(['LEDGER'], 7),
}

/** Un worker sano. Los otros tres estados se revisan cambiando este objeto. */
export const commsHealth = {
  worker: {
    last_tick_at: new Date(Date.now() - 20_000).toISOString(),
    backend: 'ses',
    send_rate: 25,
    daily_sent: 312,
    daily_quota: 35000,
    production_access: true,
  },
  meta: meta(['LEDGER'], 4),
}

export const commsWebinars = {
  rows: [
    { id: 'w-1', title: 'Cómo optimizar una estrategia',
      description: 'Una sesión en vivo de 45 minutos.',
      starts_at: '2026-09-10T16:00:00Z', display_timezone: 'America/Mexico_City',
      duration_minutes: 45, presenter: 'Sergio',
      join_url: 'https://meet.google.com/abc-defg-hij', recording_url: null,
      materials_url: null, capacity: null, registration_closes_at: null,
      created_at: '2026-08-30T12:00:00Z', created_by: 'u-1', cancelled_at: null,
      registered: 31, attended: 0 },
    // Ya pasó: la diferencia entre registrados y asistentes es el dato.
    { id: 'w-2', title: 'Primeros pasos con Portfolio Manager',
      description: 'Grabado.', starts_at: '2026-08-14T16:00:00Z',
      display_timezone: 'America/Mexico_City', duration_minutes: 40,
      presenter: 'Ana', join_url: null,
      recording_url: 'https://drive.google.com/file/d/xyz', materials_url: null,
      capacity: 100, registration_closes_at: '2026-08-14T15:00:00Z',
      created_at: '2026-08-01T12:00:00Z', created_by: 'u-2', cancelled_at: null,
      registered: 42, attended: 27 },
  ],
  meta: meta(['LEDGER'], 11),
}

export const commsWebinarRegistrations = {
  rows: [
    { normalized_email: 'diego@ejemplo.com', email: 'diego@ejemplo.com',
      display_name: 'Diego Ruiz', contact_id: 'ct-1',
      registered_at: '2026-08-05T10:00:00Z', source: 'email_link',
      attended: false, attendance_source: null, attended_at: null, cancelled_at: null },
    // Sin cuenta: exactamente la población que un webinar sirve para reactivar.
    { normalized_email: 'nadie@ejemplo.com', email: 'nadie@ejemplo.com',
      display_name: null, contact_id: null,
      registered_at: '2026-08-06T11:30:00Z', source: 'email_link',
      attended: true, attendance_source: 'manual_import',
      attended_at: '2026-08-14T16:05:00Z', cancelled_at: null },
    { normalized_email: 'beto@ejemplo.com', email: 'beto@ejemplo.com',
      display_name: 'Beto Salas', contact_id: 'ct-2',
      registered_at: '2026-08-04T09:00:00Z', source: 'in_app',
      attended: false, attendance_source: null, attended_at: null,
      cancelled_at: '2026-08-13T18:00:00Z' },
  ],
  meta: meta(['LEDGER'], 9),
}

// ── soporte ─────────────────────────────────────────────────────────────────
//
// Poco halagador, a propósito, como el resto de este archivo. Una cola que se
// ve sana enseñaría a leer mal la pantalla: aquí hay un vencido de hace tres
// días, uno discutido en privado y nunca contestado, uno sin organización — la
// persona más probable de estar escribiendo «no puedo entrar a ninguna
// organización» — y un p90 de 31 horas contra un p50 de 41 minutos, que es la
// forma en que un promedio esconde a quien se quedó esperando.
const hoursAgo = (h: number) => new Date(Date.parse('2026-09-07T18:00:00Z') - h * 3_600_000).toISOString()

/**
 * Un censo incómodo, como manda este archivo: una serie completa que saldría
 * de circulación, un ticker con 61 barras que escalaría a revisión humana, y
 * un artefacto con volumen 9× y pares al 14% — que es la fila que dice que el
 * umbral está mal, no el dato. `touches_money_known: false` para que la
 * advertencia se vea en fixtures, que es donde alguien la va a leer primero.
 */
export const priceCensus = {
  meta: meta(['MODELED'], 71),
  census_at: '2026-09-09T03:12:44Z',
  total_rows: 4_812,
  headline: [
    { verdict: 'artifact', rows: 3_105, tickers: 412, pct: 64.5 },
    { verdict: 'real_move', rows: 1_290, tickers: 388, pct: 26.8 },
    { verdict: 'indeterminate', rows: 391, tickers: 140, pct: 8.1 },
    { verdict: 'no_context', rows: 26, tickers: 19, pct: 0.5 },
  ],
  series: [{ ticker_id: 8812, ticker: 'XPLR', rule: 'dust_series', anomaly_date: '2026-06-03' }],
  escalations: [
    { ticker_id: 2210, ticker: 'HMNY', bars: 61, from: '2025-11-02', to: '2026-04-19' },
    { ticker_id: 9034, ticker: 'BTCS', bars: 23, from: '2026-02-14', to: '2026-05-30' },
  ],
  by_rule: [
    { rule: 'reverted_spike', bars: 2_410, tickers: 380 },
    { rule: 'stale_repeat', bars: 512, tickers: 61 },
    { rule: 'terminal_unconfirmed', bars: 182, tickers: 44 },
    { rule: 'dust_series', bars: 1, tickers: 1 },
  ],
  by_status: [
    { flag_status: 'persistent', kind: 'jump', verdict: 'artifact', rows: 2_880 },
    { flag_status: 'persistent', kind: 'jump', verdict: 'real_move', rows: 1_102 },
    { flag_status: 'new', kind: 'jump', verdict: 'artifact', rows: 225 },
    { flag_status: 'healing', kind: 'gap', verdict: 'indeterminate', rows: 391 },
  ],
  evidence: [
    { ticker: 'XPLR', anomaly_date: '2026-06-03', rule: 'dust_series', prev_close: 4.12, close: 0.0004, ratio: 0.0, volume_x: 0.1, peer_pct: 0.3 },
    { ticker: 'NVDA', anomaly_date: '2026-03-18', rule: 'reverted_spike', prev_close: 118.4, close: 71.02, ratio: 0.6, volume_x: 9.2, peer_pct: 14.1 },
    { ticker: 'HMNY', anomaly_date: '2026-01-09', rule: 'stale_repeat', prev_close: 0.31, close: 0.31, ratio: 1.0, volume_x: 0.0, peer_pct: 1.1 },
  ],
  escalations_limit: 30,
  by_status_limit: 25,
  evidence_limit: 25,
  touches_money_known: false,
}

export const supportQueue: SupportQueue = {
  meta: meta(['LEDGER', 'EXTERNAL'], 38),
  filtered_by: 'unanswered · all · any',
  counts: {
    live: 9,
    open: 6,
    awaiting_user: 3,
    resolved: 14,
    closed: 31,
    unanswered: 4,
    unassigned: 3,
    breached: 1,
    oldest_unanswered_seconds: 72 * 3600,
    first_response_p50_seconds: 41 * 60,
    first_response_p90_seconds: 31 * 3600,
    reopened: 2,
  },
  rows: [
    {
      ticket_id: '11111111-1111-1111-1111-111111111111',
      reference: 'FIN-1042',
      subject: 'El estudio se quedó sin memoria y no sé qué reducir',
      kind: 'problem',
      status: 'open',
      priority: 'high',
      organization_id: 'aaaaaaaa-0000-0000-0000-000000000001',
      org_name: 'Momento Capital',
      org_class: 'external',
      requester_user_id: 'bbbbbbbb-0000-0000-0000-000000000001',
      requester_email: 'ana@momentocapital.mx',
      assigned_staff_id: null,
      created_at: hoursAgo(72),
      last_customer_message_at: hoursAgo(72),
      last_activity_at: hoursAgo(72),
      first_response_due_at: hoursAgo(68),
      first_response_at: null,
      message_count: 1,
      never_answered: true,
      breached: true,
      at_risk: false,
    },
    {
      ticket_id: '22222222-2222-2222-2222-222222222222',
      reference: 'FIN-1044',
      subject: 'Me cobraron dos veces el mismo paquete',
      kind: 'billing',
      status: 'open',
      priority: 'urgent',
      organization_id: 'aaaaaaaa-0000-0000-0000-000000000002',
      org_name: 'Tártaro',
      org_class: 'external',
      requester_user_id: 'bbbbbbbb-0000-0000-0000-000000000002',
      requester_email: 'luis@tartaro.io',
      assigned_staff_id: 'cccccccc-0000-0000-0000-000000000001',
      created_at: hoursAgo(3),
      last_customer_message_at: hoursAgo(3),
      last_activity_at: hoursAgo(2),
      first_response_due_at: hoursAgo(-1),
      first_response_at: null,
      message_count: 2,
      never_answered: true,
      breached: false,
      at_risk: true,
    },
    {
      ticket_id: '33333333-3333-3333-3333-333333333333',
      reference: 'FIN-1047',
      subject: 'No puedo entrar a ninguna organización',
      kind: 'question',
      status: 'open',
      priority: 'normal',
      organization_id: null,
      org_name: null,
      org_class: null,
      requester_user_id: 'bbbbbbbb-0000-0000-0000-000000000003',
      requester_email: null,
      assigned_staff_id: null,
      created_at: hoursAgo(20),
      last_customer_message_at: hoursAgo(20),
      last_activity_at: hoursAgo(20),
      first_response_due_at: hoursAgo(-4),
      first_response_at: null,
      message_count: 1,
      never_answered: true,
      breached: false,
      at_risk: false,
    },
    {
      ticket_id: '44444444-4444-4444-4444-444444444444',
      reference: 'FIN-1039',
      subject: 'Sugerencia: recordar el último Asset Group',
      kind: 'idea',
      status: 'awaiting_user',
      priority: 'low',
      organization_id: 'aaaaaaaa-0000-0000-0000-000000000003',
      org_name: 'Fintela',
      org_class: 'internal',
      requester_user_id: 'bbbbbbbb-0000-0000-0000-000000000004',
      requester_email: 'dev@fintela.io',
      assigned_staff_id: 'cccccccc-0000-0000-0000-000000000001',
      created_at: hoursAgo(260),
      last_customer_message_at: hoursAgo(255),
      last_activity_at: hoursAgo(250),
      first_response_due_at: null,
      first_response_at: hoursAgo(254),
      message_count: 4,
      never_answered: false,
      breached: false,
      at_risk: false,
    },
  ],
}

export const supportTicket: SupportTicketDetail = {
  meta: meta(['LEDGER', 'EXTERNAL'], 19),
  ticket_id: '11111111-1111-1111-1111-111111111111',
  reference: 'FIN-1042',
  subject: 'El estudio se quedó sin memoria y no sé qué reducir',
  kind: 'problem',
  status: 'open',
  priority: 'high',
  // Distinto del derivado a propósito: es el caso que la pantalla existe para
  // hacer visible, y con fixtures sanos nadie lo vería nunca.
  derived_priority: 'normal',
  visibility: 'private',
  source: 'web',
  organization_id: 'aaaaaaaa-0000-0000-0000-000000000001',
  org_name: 'Momento Capital',
  org_class: 'external',
  requester_user_id: 'bbbbbbbb-0000-0000-0000-000000000001',
  requester_email: 'ana@momentocapital.mx',
  assigned_staff_id: null,
  assigned_at: null,
  created_at: hoursAgo(72),
  updated_at: hoursAgo(70),
  first_response_due_at: hoursAgo(68),
  next_response_due_at: null,
  first_response_at: null,
  last_customer_message_at: hoursAgo(72),
  last_staff_message_at: null,
  last_activity_at: hoursAgo(70),
  resolved_at: null,
  closed_at: null,
  closed_reason: null,
  reopen_count: 0,
  page_url: '/analysis/studies/4213',
  app_version: '2026.09.01',
  message_count: 1,
  // Cero: se habló del ticket en privado y nunca se le habló al cliente. Es la
  // señal de rezago más condenatoria que esta pantalla puede mostrar.
  staff_message_count: 0,
  total_chars: 1180,
  messages: [
    {
      message_id: 'dddddddd-0000-0000-0000-000000000001',
      seq: 1,
      author_kind: 'user',
      author_id: 'bbbbbbbb-0000-0000-0000-000000000001',
      source: 'web',
      created_at: hoursAgo(72),
      body_chars: 1180,
    },
  ],
  timeline: [
    {
      event_id: 2,
      occurred_at: hoursAgo(70),
      action: 'note_added',
      actor_kind: 'staff',
      actor_id: 'cccccccc-0000-0000-0000-000000000001',
      from_value: null,
      to_value: null,
      via: 'internal_console',
    },
    {
      event_id: 1,
      occurred_at: hoursAgo(72),
      action: 'created',
      actor_kind: 'user',
      actor_id: 'bbbbbbbb-0000-0000-0000-000000000001',
      from_value: null,
      to_value: 'problem',
      via: 'spa',
    },
  ],
}

/**
 * El hilo, con una nota interna dentro.
 *
 * La nota está a propósito: es el único estado donde esta pantalla tiene que
 * distinguir visualmente lo que el cliente ya leyó de lo que no debe leer nunca,
 * y con fixtures «limpios» nadie lo revisaría hasta que un cliente lo causara.
 */
export const supportThread: SupportThread = {
  messages: [
    {
      id: 'eeeeeeee-0000-0000-0000-000000000001',
      seq: 1,
      visibility: 'customer',
      author_kind: 'user',
      author_name: 'Ana',
      body: 'Lancé el estudio con 400 tickers y se detuvo diciendo que se quedó sin memoria. Reduje a 200 y pasó lo mismo. ¿Qué debería recortar?',
      created_at: '2026-09-04T18:00:00Z',
    },
  ],
  notes: [
    {
      id: 'eeeeeeee-0000-0000-0000-000000000002',
      seq: null,
      visibility: 'internal',
      author_kind: 'staff',
      author_name: 'ana@fintela.io',
      body: 'El sizing de este estudio salió con task_memory_mib por debajo del p90 medido. Confirmar con cómputo antes de contestar.',
      created_at: '2026-09-04T20:00:00Z',
    },
  ],
}

// ── solicitudes de acceso ──────────────────────────────────────────────────
// Tres filas, una por cada forma en que la columna «Tipo de institución» puede
// pintarse: un código conocido, otro código conocido, y ninguno — la persona
// invitada por una organización no llena formulario, así que su `application`
// es `{}` y la columna muestra la raya. La primera además dispara las dos
// señales que hacen que aprobar pida dos firmas.
export const accessQueue: AccessQueue = {
  meta: meta(['LEDGER'], 12),
  rows: [
    {
      user_id: 'aaaaaaaa-1111-4111-8111-111111111111',
      state: 'pending',
      state_expires_at: null,
      email: 'ana.buda@gmail.com',
      locale: 'es',
      application: {
        full_name: 'Ana Buda',
        org_name: 'Momento Capital',
        institution_type: 'asset_manager',
        occupation: 'Head of Quantitative Research',
        purpose:
          'Evaluación de estrategias sistemáticas de renta variable y simulación de portafolios antes de pasarlos a operación.',
      },
      requested_at: '2026-09-10T14:20:00Z',
      days_waiting: 4,
      decided_by: null,
      decided_at: null,
      reason_code: null,
      note: null,
      duplicate_of_refused: true,
      free_mail_domain: true,
    },
    {
      user_id: 'bbbbbbbb-2222-4222-8222-222222222222',
      state: 'pending',
      state_expires_at: null,
      email: 'jlopez@itam.mx',
      locale: 'es',
      application: {
        full_name: 'Julián López',
        org_name: 'ITAM — Departamento de Finanzas',
        institution_type: 'academic',
        occupation: 'Profesor investigador',
        purpose: 'Material para un curso de posgrado en gestión cuantitativa de portafolios.',
      },
      requested_at: '2026-09-13T09:05:00Z',
      days_waiting: 1,
      decided_by: null,
      decided_at: null,
      reason_code: null,
      note: null,
      duplicate_of_refused: false,
      free_mail_domain: false,
    },
    {
      user_id: 'cccccccc-3333-4333-8333-333333333333',
      state: 'pending',
      state_expires_at: null,
      email: 'm.rios@northlakefo.com',
      locale: 'en',
      application: {},
      requested_at: '2026-09-14T08:40:00Z',
      days_waiting: 0,
      decided_by: null,
      decided_at: null,
      reason_code: null,
      note: null,
      duplicate_of_refused: false,
      free_mail_domain: false,
    },
  ],
}

export const accessHistory: AccessHistory = {
  meta: meta(['LEDGER'], 9),
  rows: [
    {
      id: 1,
      user_id: 'aaaaaaaa-1111-4111-8111-111111111111',
      from_state: null,
      to_state: 'pending',
      reason_code: null,
      note: null,
      actor_kind: 'self',
      actor_id: null,
      request_id: null,
      occurred_at: '2026-09-10T14:20:00Z',
      notify_kind: 'access_requested',
      notify_sent_at: '2026-09-10T14:21:07Z',
      notify_attempts: 1,
      notify_error: null,
    },
  ],
}

// El catálogo que `AccessPage` usa para ofrecer sólo las acciones legales desde
// el estado actual. Una de cada, con los motivos del CHECK del producto.
export const accessActions: AccessAction[] = [
  {
    key: 'access.approve',
    capability: 'users:approve',
    to_state: 'approved',
    from_states: ['pending', 'rejected'],
    reasons: ['approved'],
    always_dual: false,
    dual_when_risky: true,
    accepts_expiry: false,
    confirm_label: 'el correo de la persona',
    effects: [
      {
        severity: 'Info',
        code: 'access.email',
        message: 'Le llega «Tu cuenta de Fintela ya está lista».',
        reversible: true,
        latency: 'minutos',
      },
    ],
    permitted: true,
  },
  {
    key: 'access.reject',
    capability: 'users:approve',
    to_state: 'rejected',
    from_states: ['pending'],
    reasons: ['not_a_fit', 'incomplete_information', 'suspected_duplicate', 'abuse'],
    always_dual: false,
    dual_when_risky: false,
    accepts_expiry: false,
    confirm_label: 'el correo de la persona',
    effects: [
      {
        severity: 'Warn',
        code: 'access.email',
        message: 'Le llega el motivo elegido, redactado en su idioma.',
        reversible: true,
        latency: 'minutos',
      },
    ],
    permitted: true,
  },
  {
    key: 'access.suspend',
    capability: 'users:approve',
    to_state: 'suspended',
    from_states: ['approved'],
    reasons: ['payment_issue', 'abuse', 'customer_request'],
    always_dual: true,
    dual_when_risky: false,
    accepts_expiry: true,
    confirm_label: 'el correo de la persona',
    effects: [
      {
        severity: 'Danger',
        code: 'access.block',
        message: 'Deja de poder usar Fintela en menos de medio minuto; sus llaves de API también.',
        reversible: true,
        latency: '30 s',
      },
    ],
    permitted: true,
  },
]

// ── contactos del sitio ────────────────────────────────────────────────────
// Tres filas, una por puerta, y una por cada forma en que «Aviso al staff»
// puede pintarse: avisado a dos personas, en cola con un error de reintento, y
// uno que ya no lo intentará. La primera lleva más de un día esperando.
export const contactQueue: ContactQueue = {
  meta: meta(['LEDGER'], 6),
  rows: [
    {
      id: 'cccccccc-1111-4111-8111-111111111111',
      kind: 'walkthrough',
      name: 'Ana Ruiz',
      company: 'Fondo XYZ',
      email: 'ana.ruiz@example.com',
      phone: '+52 55 1234 5678',
      message:
        'Corremos momentum en renta variable de EU y queremos ver cómo se simula un portafolio con costos de transacción.\n\nPodemos el jueves.',
      locale: 'es',
      page_url: 'https://fintela.io/contact?intent=walkthrough',
      status: 'new',
      handled_by: null,
      handled_at: null,
      note: null,
      created_at: '2026-09-13T09:10:00Z',
      hours_waiting: 31,
      notify_state: 'sent',
      staff_notify_attempts: 1,
      staff_notified_at: '2026-09-13T09:10:40Z',
      staff_notified_to: ['myrna.ortiz@fintela.io', 'sergio.montes@fintela.io'],
      staff_notify_error: null,
    },
    {
      id: 'cccccccc-2222-4222-8222-222222222222',
      kind: 'demo',
      name: 'John Park',
      company: 'Park Capital',
      email: 'john@parkcap.io',
      phone: '',
      message: 'Interested in the optimizer for a small equities book. What does onboarding look like?',
      locale: 'en',
      page_url: 'https://fintela.io/contact',
      status: 'new',
      handled_by: null,
      handled_at: null,
      note: null,
      created_at: '2026-09-14T15:02:00Z',
      hours_waiting: 1,
      notify_state: 'pending',
      staff_notify_attempts: 2,
      staff_notified_at: null,
      staff_notified_to: [],
      staff_notify_error: 'sergio.montes@fintela.io: transient: SES throttled',
    },
    {
      id: 'cccccccc-3333-4333-8333-333333333333',
      kind: 'support',
      name: 'Beatriz Lima',
      company: 'Lima Gestão',
      email: 'beatriz@limagestao.com.br',
      phone: '+55 11 99999 0000',
      message: 'Não consigo redefinir minha senha; o e-mail nunca chega.',
      locale: 'pt',
      page_url: 'https://fintela.io/contact',
      status: 'new',
      handled_by: null,
      handled_at: null,
      note: null,
      created_at: '2026-09-14T12:40:00Z',
      hours_waiting: 3,
      notify_state: 'failed',
      staff_notify_attempts: 5,
      staff_notified_at: null,
      staff_notified_to: [],
      staff_notify_error: 'no recipients: platform_staff has no live rows and CONTACT_NOTIFY_TO is unset',
    },
  ],
}

export const contactCounts: ContactCounts = {
  meta: meta(['LEDGER'], 3),
  new: 3,
  handled: 12,
  discarded: 4,
  unnotified: 2,
  oldest_new_seconds: 31 * 3600,
}

/** Espeja el catálogo de internal-admin (`actions/contact.rs`). */
export const contactActions: ContactAction[] = [
  {
    key: 'contact.handle',
    capability: 'support:triage',
    to_status: 'handled',
    from_statuses: ['new', 'discarded'],
    confirm_label: 'el correo del remitente',
    effects: [
      {
        severity: 'Info',
        code: 'no_email_leaves',
        message: 'No manda ningún correo. Sólo saca el mensaje de la cola de nuevos.',
        reversible: true,
        latency: 'inmediato',
      },
    ],
    permitted: true,
  },
  {
    key: 'contact.discard',
    capability: 'support:triage',
    to_status: 'discarded',
    from_statuses: ['new', 'handled'],
    confirm_label: 'el correo del remitente',
    effects: [
      {
        severity: 'Warn',
        code: 'nothing_is_deleted',
        message: 'No borra el mensaje: queda en «Descartados» con tu nota.',
        reversible: true,
        latency: '—',
      },
    ],
    permitted: true,
  },
]
